/**
 * visions.ts — canon source of truth for the three Elder Making-Biscuits
 * vision sequences.
 *
 * Design reference: `docs/specs/SPEC-making-biscuits.md` §5 "Per-Elder
 * Vision Content". All lines are either **verbatim** from the game-book
 * (`docs/game-book/book.html`) or one-step paraphrases that preserve the
 * canonical cadence. **Do not invent new lore here** — subsequent
 * iterations should also pull from the 2026-04-14 Companion Book PDF.
 *
 * The book citations below are kept next to each line so reviewers can
 * verify the canon without opening the PDF.
 */

/** Imagery token for a beat — referenced by `VisionRenderer` to pick
 *  a reusable scene-decoration quad. Kept abstract so PR2's "2D overlay"
 *  implementation and a later 3D scene-insert implementation can share
 *  the same data. */
export type VisionImagery =
  | "starryVoid"       // starry-black sky, scrap falling
  | "nickedEarEmber"   // close-up ear nick, dust-in-fur ember tone
  | "leatherChair"     // Gus's parlor chair + lamp warmth
  | "bowlSteamClock"   // bowl of mist, slow clockwork second-hand
  | "peonyYouth"       // young Leo among peonies
  | "argyleSilhouette" // cream-tabby silhouette in green argyle sweater
  | "none";            // pure-text beat, no imagery

export interface VisionBeat {
  /** Whispered line. Blank string for an image-only beat. */
  line: string;
  /** Canon citation — either `book.html:N` or "companion PDF §X". */
  cite: string;
  /** Background imagery token. `"none"` renders text-only. */
  imagery: VisionImagery;
}

export interface VisionSequence {
  /** Elder identifier matching `SaveSystem.BiscuitElder`. */
  elder: "leo" | "gus" | "kitty";
  /** Title shown in the exit toast + codex. */
  title: string;
  /**
   * Ordered beats. First-time visions run all 4; replays run only the
   * beats marked `echo: true` (kept to 2 to avoid replaying the full
   * prose every time Minna walks back to the anchor).
   */
  beats: VisionBeat[];
  /**
   * Which beat indices to replay on a revisit. Indices are zero-based
   * and reference the `beats` array above.
   */
  replayBeats: number[];
  /** Audio cue key — routed through AudioManager for the per-Elder sting. */
  audioCue: string;
}

/**
 * Elder Kitty — "The Rust Remembers"
 * Source: `docs/game-book/book.html:436-446` (ELDER_KITTY_ENCOUNTER canon).
 */
export const VISION_KITTY: VisionSequence = {
  elder: "kitty",
  title: "The Rust Remembers",
  audioCue: "vision-kitty-ember",
  beats: [
    {
      line: "",
      cite: "book.html:439 — 'the scrap falls away into a starry void'",
      imagery: "starryVoid",
    },
    {
      // Canon paraphrase of the "rust grows back" imagery in Kitty's
      // scrapyard canon. Encounter line: "You cannot force a blocked
      // gear" — the biscuits prelude distills the imagery to a sigh.
      line: "The rust grows back.",
      cite: "book.html:441 paraphrase",
      imagery: "nickedEarEmber",
    },
    {
      line: "",
      cite: "book.html:439 — 'rugged, battle-scarred tomcat'",
      imagery: "nickedEarEmber",
    },
    {
      // Verbatim from Elder Kitty encounter (src-babylon/config/dialogue.ts:126).
      line: "Stillness before motion. Weight before will.",
      cite: "ELDER_KITTY_ENCOUNTER line 5 (book.html:441 expanded)",
      imagery: "none",
    },
  ],
  replayBeats: [1, 3],
};

/**
 * Elder Gus — "The Chair, The Dough, The Breath"
 * Source: `docs/game-book/book.html:499-523` (ELDER_GUS_ENCOUNTER canon).
 */
export const VISION_GUS: VisionSequence = {
  elder: "gus",
  title: "The Chair, The Dough, The Breath",
  audioCue: "vision-gus-clock",
  beats: [
    {
      line: "",
      cite: "book.html:502 — 'quiet, plush parlor… distinguished tabby'",
      imagery: "leatherChair",
    },
    {
      // Paraphrase of book.html:514 — "calm as habit, not as effort".
      line: "Calm as habit, not as effort.",
      cite: "book.html:514 verbatim",
      imagery: "leatherChair",
    },
    {
      line: "",
      cite: "book.html:515 — 'stop moving first, let the room slow down'",
      imagery: "bowlSteamClock",
    },
    {
      // Verbatim from ELDER_GUS_ENCOUNTER line 1 (dialogue.ts:149).
      line: "Respect the stillness between beats.",
      cite: "ELDER_GUS_ENCOUNTER line 1",
      imagery: "none",
    },
  ],
  replayBeats: [1, 3],
};

/**
 * Grand Elder Leo — "The Mirror and the Peonies"
 * Source: `docs/game-book/book.html:569-612` (ELDER_LEO_CANOPY_ENCOUNTER
 * and the closing stanza quoted verbatim on book.html:586-592).
 */
export const VISION_LEO: VisionSequence = {
  elder: "leo",
  title: "The Mirror and the Peonies",
  audioCue: "vision-leo-peony",
  beats: [
    {
      line: "",
      cite: "book.html:608 — 'young Leo among peonies'",
      imagery: "peonyYouth",
    },
    {
      // Paraphrase of book.html:604 — "the teaching was always simple,
      // the teacher is the one waiting for you to arrive at it".
      line: "The teaching was always simple.",
      cite: "book.html:604 paraphrase",
      imagery: "peonyYouth",
    },
    {
      line: "",
      cite: "book.html:603 — 'cream-tabby gentleman in a green argyle sweater'",
      imagery: "argyleSilhouette",
    },
    {
      // Verbatim from the canon closing stanza (book.html:587).
      line: "The machine was but a mirror.",
      cite: "book.html:587 verbatim",
      imagery: "none",
    },
  ],
  replayBeats: [1, 3],
};

/** Registry of all shipping vision sequences by Elder. */
export const VISIONS: Record<"leo" | "gus" | "kitty", VisionSequence> = {
  leo: VISION_LEO,
  gus: VISION_GUS,
  kitty: VISION_KITTY,
};
